import React, { useRef, useState } from 'react';
import EmployeeOvertimePanel from './EmployeeOvertimePanel';
import { toJstDateTimeInput, type OvertimeClient } from './overtimeApi';

interface Props {
  supabaseClient: OvertimeClient | null;
  employeeCode: string;
  employeeName: string;
  disabled?: boolean;
  onSavingChange?: (saving: boolean) => void;
}

const EmployeeOvertimeHistory = ({ supabaseClient, employeeCode, employeeName, disabled, onSavingChange }: Props) => {
  const [open, setOpen] = useState(false);
  const [workDate, setWorkDate] = useState(toJstDateTimeInput(new Date().toISOString()).slice(0, 10));
  const [attendanceIds, setAttendanceIds] = useState<string[] | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const requestId = useRef(0);
  const load = async () => {
    if (!supabaseClient || !workDate || disabled) return;
    const id = ++requestId.current;
    setLoading(true);
    setError(null);
    setAttendanceIds(null);
    try {
      const { data, error: readError } = await supabaseClient.from('attendance_records')
        .select('id, clock_out_at').eq('employee_code', employeeCode).eq('work_date', workDate).limit(20);
      if (readError) throw readError;
      if (id !== requestId.current) return;
      const rows = (data ?? []).filter((row: any) => row.clock_out_at && row.id);
      setAttendanceIds(rows.map((row: any) => String(row.id)));
    } catch (err) {
      if (id === requestId.current) setError('過去の勤怠を取得できませんでした。通信状態を確認して再試行してください。');
    } finally {
      if (id === requestId.current) setLoading(false);
    }
  };
  return (
    <div className="mt-5 border-t border-gray-200 pt-4 text-left">
      <button type="button" disabled={disabled} aria-expanded={open} onClick={() => setOpen(value => !value)} className="text-sm text-blue-700 underline disabled:text-gray-400">過去の勤務・時間外報告を確認</button>
      {open && <div className="mt-3">
        <div className="flex flex-wrap items-end gap-2">
          <label className="text-sm">勤務日
            <input aria-label="過去の勤務日" type="date" value={workDate} max={toJstDateTimeInput(new Date().toISOString()).slice(0, 10)} disabled={disabled || loading}
              onChange={event => { requestId.current += 1; setWorkDate(event.target.value); setAttendanceIds(null); setError(null); }} className="mt-1 block rounded border border-gray-300 p-2" />
          </label>
          <button type="button" onClick={load} disabled={disabled || loading || !workDate || !supabaseClient} className="rounded border border-blue-400 px-3 py-2 text-blue-800 disabled:opacity-50">{loading ? '確認中…' : '勤怠を確認する'}</button>
        </div>
        {error && <p role="alert" className="mt-2 text-sm text-red-700">{error}</p>}
        {attendanceIds?.length === 0 && <p className="mt-2 text-sm text-gray-600">この勤務日に退勤済みの記録はありません。</p>}
        {attendanceIds?.map(attendanceId => <EmployeeOvertimePanel key={`${employeeCode}-${attendanceId}`} supabaseClient={supabaseClient} employeeCode={employeeCode} employeeName={employeeName} attendanceId={attendanceId} onSavingChange={onSavingChange} />)}
      </div>}
    </div>
  );
};

export default EmployeeOvertimeHistory;
